import { collection, getDocs, query, where } from 'firebase/firestore';

const GOOGLE_EVENT_PREFIX = 'google_';
const TIME_MATCH_WINDOW_MS = 2 * 60 * 60 * 1000;
const DEV = process.env.NODE_ENV === 'development';

export function buildCalendarEventMatchKeys(event) {
  const keys = new Set();

  const addKey = (raw) => {
    if (raw == null || raw === '') return;
    const s = String(raw);
    keys.add(s);
    // Also add with prefix stripped
    if (s.startsWith(GOOGLE_EVENT_PREFIX)) {
      keys.add(s.slice(GOOGLE_EVENT_PREFIX.length));
    }
    // Also add with prefix prepended (in case stored without it)
    if (!s.startsWith(GOOGLE_EVENT_PREFIX)) {
      keys.add(`${GOOGLE_EVENT_PREFIX}${s}`);
    }
  };

  addKey(event?.id);
  addKey(event?.googleEventId);

  return [...keys];
}

export function normalizeMeetingTitle(title) {
  if (!title) return '';
  return String(title)
    .replace(/^\d{1,2}:\d{2}\s*(?:AM|PM)\s*-\s*/i, '')
    .trim()
    .toLowerCase();
}

function parseMeetingSummaryStart(data) {
  const value = data?.scheduledStartTime;
  if (!value) return null;
  if (typeof value.toDate === 'function') return value.toDate();
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

// Returns true (titles agree), false (titles disagree), or null (one side missing —
// no information either way; an empty title must never count as a positive match).
function meetingTitlesMatch(summaryTitle, eventTitle) {
  const a = normalizeMeetingTitle(summaryTitle);
  const b = normalizeMeetingTitle(eventTitle);
  if (!a || !b) return null;
  if (a === b) return true;
  if (a.includes(b) || b.includes(a)) return true;
  return a.replace(/\s+/g, '') === b.replace(/\s+/g, '');
}

function meetingTitlesMatchExactly(summaryTitle, eventTitle) {
  const a = normalizeMeetingTitle(summaryTitle);
  const b = normalizeMeetingTitle(eventTitle);
  if (!a || !b) return false;
  return a === b || a.replace(/\s+/g, '') === b.replace(/\s+/g, '');
}

function normalizeEmail(email) {
  const s = String(email || '').trim().toLowerCase();
  return s.includes('@') ? s : '';
}

function getEventAttendeeEmails(event) {
  const emails = new Set();
  const add = (e) => {
    const n = normalizeEmail(e);
    if (n) emails.add(n);
  };
  for (const a of event?.attendees || []) add(a?.email);
  for (const inv of event?.clientInvitees || []) add(inv?.email);
  add(event?.organizer?.email);
  add(event?.creator?.email);
  return emails;
}

function getSummaryInviteeEmails(summary) {
  const emails = new Set();
  for (const e of summary?.inviteeEmails || []) {
    const n = normalizeEmail(e);
    if (n) emails.add(n);
  }
  const rec = normalizeEmail(summary?.recordedByEmail);
  if (rec) emails.add(rec);
  return emails;
}

/**
 * Compare the people on the calendar event with the people on the recorded call.
 * Returns true (a shared participant confirms the match), false (both sides have
 * participant data but nobody overlaps — this is definitively a different call),
 * or null (one side has no usable participant data, so no signal either way).
 *
 * When internalEmails is provided, overlap is checked on NON-internal (client-side)
 * participants first — every call recorded by the same team member shares the
 * recorder's email, so internal-only overlap can't distinguish two clients' calls.
 */
function attendeeEmailOverlap(event, summary, internalEmailSet) {
  const summaryEmails = getSummaryInviteeEmails(summary);
  if (!summaryEmails.size) return null;

  const eventEmails = getEventAttendeeEmails(event);
  if (!eventEmails.size) return null;

  if (internalEmailSet && internalEmailSet.size) {
    const clientSideEventEmails = [...eventEmails].filter((e) => !internalEmailSet.has(e));
    if (clientSideEventEmails.length) {
      return clientSideEventEmails.some((e) => summaryEmails.has(e));
    }
    // Event copy lists only internal attendees — no discriminating data.
    return null;
  }

  return [...eventEmails].some((e) => summaryEmails.has(e));
}

export function summaryHasContent(summary) {
  if (!summary) return false;
  return !!(
    summary.summary ||
    summary.transcription ||
    (summary.keyPoints && summary.keyPoints.length) ||
    (summary.actionItems && summary.actionItems.length)
  );
}

function findSummaryByEventIdsFromCache(event, eventKeys, summaries, label, internalEmailSet) {
  for (const eventId of eventKeys) {
    const candidates = summaries.filter(
      (summary) => summary.eventId === eventId || summary.googleEventId === eventId
    );
    for (const found of candidates) {
      // The webhook's Google-event-ID resolution is itself fuzzy, so a wrong ID can be
      // stamped on a summary. If the people on the event and the people on the call
      // definitively don't overlap, the ID is a bad stamp — reject it.
      const overlap = attendeeEmailOverlap(event, found, internalEmailSet);
      if (overlap === false) {
        if (DEV) {
          console.log(
            `[SummaryLookup] ${label}: eventId "${eventId}" matched ${found.id} but attendees don't overlap — rejected`
          );
        }
        continue;
      }
      if (DEV) console.log(`[SummaryLookup] ${label}: matched by eventId "${eventId}"`, found.id);
      return found;
    }
  }
  return null;
}

function findSummaryByAccountAndTimeFromCache(event, summaries, label, { accountId, internalEmailSet } = {}) {
  const eventStart = new Date(event?.start || event?.startTime);
  if (Number.isNaN(eventStart.getTime())) {
    if (DEV) console.log(`[SummaryLookup] ${label}: invalid event start time`, event?.start, event?.startTime);
    return null;
  }

  const eventTitle = event?.title || event?.summary || event?.meetingTitle || '';
  let best = null;
  let bestScore = Infinity;

  for (const summary of summaries) {
    const summaryStart = parseMeetingSummaryStart(summary);
    if (!summaryStart) continue;

    const delta = Math.abs(summaryStart.getTime() - eventStart.getTime());
    if (delta > TIME_MATCH_WINDOW_MS) continue;

    const overlap = attendeeEmailOverlap(event, summary, internalEmailSet);
    const titleMatch = meetingTitlesMatch(summary.meetingTitle, eventTitle);

    // Hard gates — time proximity alone is never enough to claim a summary:
    // 1. Participants definitively don't overlap → different call. The only escape is
    //    an exact title match within 15 minutes (covers incomplete invitee lists).
    if (overlap === false) {
      const exactTitleClose =
        meetingTitlesMatchExactly(summary.meetingTitle, eventTitle) && delta <= 15 * 60 * 1000;
      if (!exactTitleClose) continue;
    }
    // 2. No participant confirmation → require the titles to affirmatively agree.
    if (overlap !== true && titleMatch !== true) continue;
    // 3. Summaries the webhook couldn't tie to this account are only trusted when a
    //    shared client-side participant confirms the call belongs on this event.
    if (accountId && summary.accountId !== accountId && overlap !== true) continue;

    const score = delta + (titleMatch === true ? 0 : 30 * 60 * 1000);
    if (score < bestScore) {
      bestScore = score;
      best = summary;
    }
  }

  if (!best) {
    if (DEV) {
      const candidates = summaries.filter((s) => {
        const t = parseMeetingSummaryStart(s);
        return t && Math.abs(t.getTime() - eventStart.getTime()) <= TIME_MATCH_WINDOW_MS * 2;
      });
      console.log(
        `[SummaryLookup] ${label}: no time match for event at ${eventStart.toISOString()}`,
        { eventTitle, nearCandidates: candidates.length, totalSummaries: summaries.length }
      );
    }
    return null;
  }

  if (DEV) {
    console.log(
      `[SummaryLookup] ${label}: matched by time/title/attendees (score ${bestScore}ms)`,
      best.id, best.meetingTitle
    );
  }
  return best;
}

/**
 * Match a calendar event to a meeting summary from a pre-loaded cache.
 * Summaries should already be pre-filtered to the account context.
 * Summaries with accountId === null are included as candidates (may be unmatched
 * webhooks that still belong to this account's meetings) but only ever win when a
 * shared client-side attendee confirms they belong to this event.
 *
 * internalEmails: emails of internal/team users — lets the attendee-overlap check
 * ignore the recorder's own email (which appears on every call they record).
 */
export function matchEventToMeetingSummary(event, summaries = [], { accountId, internalEmails = [] } = {}) {
  if (!event) return null;

  const internalEmailSet = new Set(
    (internalEmails || []).map(normalizeEmail).filter(Boolean)
  );

  // When accountId is provided, include docs that match OR have null accountId
  // (null-accountId = webhook couldn't resolve account but might still be the right meeting)
  const scopedSummaries = accountId
    ? summaries.filter(
        (summary) => summary.accountId === accountId || summary.accountId == null
      )
    : summaries;

  const label = `event[${event.id || event.googleEventId || 'unknown'}]`;

  if (DEV) {
    console.log(
      `[SummaryLookup] ${label}: checking ${scopedSummaries.length}/${summaries.length} summaries`,
      { accountId }
    );
  }

  const eventKeys = buildCalendarEventMatchKeys(event);
  if (eventKeys.length) {
    const byEventId = findSummaryByEventIdsFromCache(
      event, eventKeys, scopedSummaries, label, internalEmailSet
    );
    if (byEventId) return byEventId;
  }

  return findSummaryByAccountAndTimeFromCache(event, scopedSummaries, label, {
    accountId,
    internalEmailSet,
  });
}

export function eventHasFathomSummary(event, summaries = [], accountId, internalEmails = []) {
  const summary = matchEventToMeetingSummary(event, summaries, { accountId, internalEmails });
  return summary?.source === 'fathom' && summaryHasContent(summary);
}

/**
 * Load meeting summaries relevant to a given account context.
 * Queries:
 *   1. All summaries where accountId == accountId (definitively matched)
 *   2. Summaries where accountId == null AND recorderUid is one of the internal users
 *      (webhook fired but account couldn't be resolved — may still match by time)
 *
 * Results are deduped by Firestore doc ID.
 */
export async function loadMeetingSummariesForContext(db, { accountId, recorderUids = [] }) {
  const byDocId = new Map();

  // Query 1: by accountId
  if (accountId) {
    const snap = await getDocs(
      query(collection(db, 'meeting_summaries'), where('accountId', '==', accountId))
    );
    snap.docs.forEach((docSnap) => {
      byDocId.set(docSnap.id, { id: docSnap.id, ...docSnap.data() });
    });
  }

  // Query 2: unmatched summaries recorded by our internal users (accountId == null)
  // Firestore 'in' supports up to 30 values; batch if needed
  const uids = [...new Set(recorderUids.filter(Boolean))];
  const BATCH = 30;
  for (let i = 0; i < uids.length; i += BATCH) {
    const batch = uids.slice(i, i + BATCH);
    try {
      const snap = await getDocs(
        query(
          collection(db, 'meeting_summaries'),
          where('recorderUid', 'in', batch),
          where('accountId', '==', null)
        )
      );
      snap.docs.forEach((docSnap) => {
        if (!byDocId.has(docSnap.id)) {
          byDocId.set(docSnap.id, { id: docSnap.id, ...docSnap.data() });
        }
      });
    } catch (e) {
      // Composite query may fail if index doesn't exist; fall back gracefully
      if (DEV) console.warn('[SummaryLookup] recorderUid+accountId==null query failed:', e.message);
    }
  }

  if (DEV) {
    console.log(
      `[SummaryLookup] loadMeetingSummariesForContext: ${byDocId.size} total summaries`,
      { accountId, recorderUidCount: uids.length }
    );
  }

  return Array.from(byDocId.values());
}

// Legacy: load only by accountId (used by LeadOverview which has no internalUsers context)
export async function loadAccountMeetingSummaries(db, accountId) {
  if (!accountId) return [];
  const snap = await getDocs(
    query(collection(db, 'meeting_summaries'), where('accountId', '==', accountId))
  );
  return snap.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }));
}

// Direct Firestore lookup (used when no cache is available)
export async function findMeetingSummaryForEvent(db, event, { accountId, internalEmails = [] } = {}) {
  const internalEmailSet = new Set(
    (internalEmails || []).map(normalizeEmail).filter(Boolean)
  );
  const eventKeys = buildCalendarEventMatchKeys(event);

  if (eventKeys.length) {
    for (const eventId of eventKeys) {
      for (const field of ['eventId', 'googleEventId']) {
        const snap = await getDocs(
          query(collection(db, 'meeting_summaries'), where(field, '==', eventId))
        );
        for (const docSnap of snap.docs) {
          const candidate = { id: docSnap.id, ...docSnap.data() };
          // Same guard as the cached path: a stamped event ID whose attendees
          // definitively don't overlap the event's is a bad stamp.
          if (attendeeEmailOverlap(event, candidate, internalEmailSet) === false) continue;
          return candidate;
        }
      }
    }
  }

  if (accountId) {
    const summaries = await loadAccountMeetingSummaries(db, accountId);
    return findSummaryByAccountAndTimeFromCache(event, summaries, 'direct-lookup', {
      accountId,
      internalEmailSet,
    });
  }

  return null;
}
